import React, { memo } from 'react';
import { ChevronUp, ChevronDown, Clipboard, CheckCircle } from 'lucide-react';
import { MarkdownRenderer, PlainTextRenderer } from '../../Renderers';

/**
 * Tool message component
 * Displays tool execution messages with expandable content
 */
const ToolMessage = memo(({ 
  message, 
  messageId, 
  copiedMessageId, 
  isExpanded, 
  toggleExpansion, 
  isStreaming, 
  onCopy, 
  index, 
  searchQuery 
}) => (
  <>
    <div className="flex items-center mb-2 px-4 pt-3 text-xs font-medium">
      <span className="mr-2">🛠️</span>
      <span className="text-neutral-600 dark:text-neutral-400" style={{ fontWeight: 'bold', paddingRight: '5px' }}>
        running {message.tool_name || message.tool_description || 'Tool'}
      </span>
      
      {!isStreaming && (
        <>
          <button 
            onClick={() => toggleExpansion(index)}
            className="ml-auto text-xs text-neutral-600 dark:text-neutral-400"
            title={isExpanded ? "Show less" : "Show more"}
            aria-label={isExpanded ? "Show less" : "Show more"}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button 
            onClick={onCopy}
            className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            title="Copy to clipboard"
            aria-label="Copy to clipboard"
          >
            {copiedMessageId === messageId ? (
              <CheckCircle size={14} className="text-semantic-success" />
            ) : (
              <Clipboard size={14} className="text-neutral-600 dark:text-neutral-400" />
            )}
          </button>
        </>
      )}
    </div>
    
    {isExpanded && (
      <div className="text-ui-text-primary px-4 pb-3">
        <div>
          {message.sender === 'user' ? (
            <PlainTextRenderer 
              content={message.content} 
              searchQuery={searchQuery} 
              messageIndex={index} 
            />
          ) : (
            <>
              {message.tool_description === 'take_browser_screenshot' ? (
                <div className="screenshot-container">
                  <div className="mt-2">
                    <img 
                      src={`${message.content}`} 
                      alt="Browser screenshot" 
                      className="w-full rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm" 
                      loading="lazy"
                    />
                  </div>
                </div>
              ) : (
                <MarkdownRenderer 
                  content={message.content} 
                  searchQuery={searchQuery} 
                  messageIndex={index} 
                />
              )}
            </>
          )}
        </div>
        
        {isStreaming && (
          <span className="inline-flex items-center ml-1 mt-2">
            <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse"></span>
            <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse animation-delay-200"></span>
            <span className="w-1.5 h-1.5 mx-0.5 bg-brand-primary rounded-full kolega-animate-pulse animation-delay-400"></span>
          </span>
        )}
      </div>
    )}
  </>
));

ToolMessage.displayName = 'ToolMessage';

export default ToolMessage;